import { useState, useRef } from 'react';

import { useDraggable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { Typography, Tooltip } from 'antd';

type AdminEvent = {
  id: string;
  title: string;
  description?: string;
  dayIndex: number;
  startHour: number;
  startMinute: number;
  durationMinutes: number;
  allDay?: boolean;
  categoryId?: string;
  location?: string;
  favorite?: boolean;
  hide?: boolean;
  date?: string;
  user?: {
    name: string;
    image_url?: string;
  };
  impact?: {
    id: number;
    name: string;
  };
  event_type?: {
    id: number;
    name: string;
  };
  origin?: string;
  company_id?: string;
  shareholder?: {
    name: string;
    quantity: number;
  };
  isMultiDay?: boolean;
};

const START_HOUR = 0;

const getAgendaColor = (agendaName: string): string => {
  const name = agendaName.toLowerCase();

  if (name.includes('acionista') || name.includes('shareholder')) {
    return '#a8a8a8'; // Azul para Acionistas
  }
  if (name.includes('setor') || name.includes('mercado')) {
    return ' #003d86'; // Roxo para Setor/Mercado
  }
  if (name.includes('companhia') || name.includes('company')) {
    return '#5c8ef9'; // Cinza para Companhia
  }
  if (name.includes('regulatório') || name.includes('cvm') || name.includes('b3')) {
    return '#FFB030'; // Amarelo para Regulatório CVM/B3
  }

  return '#005462'; // Cor padrão (azul Prisma)
};

const getEventColorByImpact = (impact?: { id: number; name: string } | number): string => {
  if (!impact) return '#005462';

  // Se for um número (ID), retorna a cor padrão
  if (typeof impact === 'number') {
    return '#005462';
  }

  return getAgendaColor(impact.name);
};

export const DraggableEvent = ({
  evt,
  onEventClick,
  layout,
}: {
  evt: AdminEvent & { overlappingEvents?: AdminEvent[]; isOverlapping?: boolean };
  onEventClick?: (event: AdminEvent) => void;
  layout?: { left: number; width: number; zIndex: number };
}) => {
  const { attributes, listeners, setNodeRef, transform } = useDraggable({ id: `evt-${evt.id}` });
  const [isDragging, setIsDragging] = useState(false);
  const [clickTimeout, setClickTimeout] = useState<NodeJS.Timeout | null>(null);
  const [mouseDownPosition, setMouseDownPosition] = useState<{ x: number; y: number } | null>(null);
  const isDraggingRef = useRef(false);

  const eventColor = getEventColorByImpact(evt.impact);

  const HOUR_HEIGHT = 35;
  const style = {
    transform: CSS.Translate.toString(transform),
    position: 'absolute' as const,
    left: layout ? `calc(${layout.left}% + 1px)` : 2,
    right: layout ? 'auto' : 2,
    width: layout ? `calc(${layout.width}% - 2px)` : 'auto',
    top: (evt.startHour - START_HOUR) * HOUR_HEIGHT + (evt.startMinute / 60) * HOUR_HEIGHT,
    height: (evt.durationMinutes / 60) * HOUR_HEIGHT,
    background: evt.isMultiDay ? 'linear-gradient(90deg, #fff 0%, #f0f8ff 100%)' : '#fff',
    color: 'var(--text-color)',
    borderRadius: evt.isMultiDay ? '6px 10px 10px 6px' : 10,
    fontSize: 12,
    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
    zIndex: layout ? layout.zIndex + 2 : 2,
    '--event-color': eventColor,
    borderLeft: evt.isMultiDay ? `4px solid ${eventColor}` : 'none',
  } as React.CSSProperties;

  const endTotal = evt.startHour * 60 + evt.startMinute + evt.durationMinutes;
  const endHour = Math.floor(endTotal / 60);
  const endMinute = endTotal % 60;

  const handleClick = (_e: React.MouseEvent) => {
    if (!isDragging && onEventClick) {
      onEventClick(evt);
    }
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    setMouseDownPosition({ x: e.clientX, y: e.clientY });
    setIsDragging(false);
    isDraggingRef.current = false;

    if (clickTimeout) {
      clearTimeout(clickTimeout);
      setClickTimeout(null);
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (mouseDownPosition) {
      const deltaX = Math.abs(e.clientX - mouseDownPosition.x);
      const deltaY = Math.abs(e.clientY - mouseDownPosition.y);

      if (deltaX > 5 || deltaY > 5) {
        setIsDragging(true);
        isDraggingRef.current = true;
        if (clickTimeout) {
          clearTimeout(clickTimeout);
          setClickTimeout(null);
        }
      }
    }
  };

  const handleMouseUp = () => {
    if (clickTimeout) {
      clearTimeout(clickTimeout);
      setClickTimeout(null);
    }
    setMouseDownPosition(null);
    setTimeout(() => {
      setIsDragging(false);
      isDraggingRef.current = false;
    }, 100);
  };

  const cardClasses = [
    'agenda-event-card',
    evt.favorite ? 'favorite' : '',
    evt.hide ? 'hidden' : '',
    evt.isOverlapping ? 'overlapping' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const showTime = evt.durationMinutes >= 60;

  const tooltipContent =
    evt.isGrouped && evt.groupedEvents && evt.groupedEvents.length > 1 ? (
      <div style={{ maxWidth: 300 }}>
        <div style={{ fontWeight: 600, marginBottom: 8 }}>
          Evento agrupado ({evt.groupedEvents.length} empresas):
          {evt.isMultiDay && <span style={{ marginLeft: 8, fontSize: '12px' }}>Multi-dia</span>}
        </div>
        <div style={{ marginBottom: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <span style={{ width: 8, height: 8, background: eventColor, borderRadius: 999 }} />
            <span style={{ fontSize: 12, color: '#fff' }}>{evt.title}</span>
          </div>
          {showTime && (
            <div style={{ fontSize: 11, color: '#ccc', marginLeft: 16 }}>
              {String(evt.startHour).padStart(2, '0')}:{String(evt.startMinute).padStart(2, '0')} -{' '}
              {String(endHour).padStart(2, '0')}:{String(endMinute).padStart(2, '0')}
            </div>
          )}
          {evt.description && (
            <div style={{ fontSize: 11, color: '#ccc', marginLeft: 16, marginTop: 4 }}>
              {evt.description}
            </div>
          )}
        </div>
      </div>
    ) : evt.isOverlapping && evt.overlappingEvents ? (
      <div style={{ maxWidth: 300 }}>
        <div style={{ fontWeight: 600, marginBottom: 8 }}>Eventos neste horário:</div>
        <div style={{ marginBottom: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <span style={{ width: 8, height: 8, background: eventColor, borderRadius: 999 }} />
            <span style={{ fontSize: 12 }}>{evt.title}</span>
          </div>
          {showTime && (
            <div style={{ fontSize: 11, color: '#ffffff', marginLeft: 16 }}>
              {String(evt.startHour).padStart(2, '0')}:{String(evt.startMinute).padStart(2, '0')} -{' '}
              {String(endHour).padStart(2, '0')}:{String(endMinute).padStart(2, '0')}
            </div>
          )}
        </div>
        {evt.overlappingEvents.map((overlappingEvent) => {
          const overlappingEventColor = getEventColorByImpact(overlappingEvent.impact);
          const overlappingEndTotal =
            overlappingEvent.startHour * 60 +
            overlappingEvent.startMinute +
            overlappingEvent.durationMinutes;
          const overlappingEndHour = Math.floor(overlappingEndTotal / 60);
          const overlappingEndMinute = overlappingEndTotal % 60;

          return (
            <div key={overlappingEvent.id} style={{ marginBottom: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                <span
                  style={{
                    width: 8,
                    height: 8,
                    background: overlappingEventColor,
                    borderRadius: 999,
                  }}
                />
                <span style={{ fontSize: 12 }}>{overlappingEvent.title}</span>
              </div>
              {overlappingEvent.durationMinutes >= 60 && (
                <div style={{ fontSize: 11, color: '#ffffff', marginLeft: 16 }}>
                  {String(overlappingEvent.startHour).padStart(2, '0')}:
                  {String(overlappingEvent.startMinute).padStart(2, '0')} -{' '}
                  {String(overlappingEndHour).padStart(2, '0')}:
                  {String(overlappingEndMinute).padStart(2, '0')}
                </div>
              )}
            </div>
          );
        })}
      </div>
    ) : evt.isMultiDay ? (
      <div style={{ maxWidth: 300 }}>
        <div style={{ fontWeight: 600, marginBottom: 8 }}>Evento Multi-dia</div>
        <div style={{ marginBottom: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <span style={{ width: 8, height: 8, background: eventColor, borderRadius: 999 }} />
            <span style={{ fontSize: 12, color: '#fff' }}>{evt.title}</span>
          </div>
          {showTime && (
            <div style={{ fontSize: 11, color: '#ccc', marginLeft: 16 }}>
              {String(evt.startHour).padStart(2, '0')}:{String(evt.startMinute).padStart(2, '0')} -{' '}
              {String(endHour).padStart(2, '0')}:{String(endMinute).padStart(2, '0')}
            </div>
          )}
          {evt.description && (
            <div style={{ fontSize: 11, color: '#ccc', marginLeft: 16, marginTop: 4 }}>
              {evt.description}
            </div>
          )}
        </div>
      </div>
    ) : null;

  return (
    <Tooltip title={tooltipContent} placement="topLeft">
      <button
        ref={setNodeRef}
        type="button"
        className={cardClasses}
        style={{
          ...style,
          border: 'none',
          outline: 'none',
          padding: '3px 4px',
          cursor: isDragging ? 'grabbing' : 'grab',
        }}
        onClick={handleClick}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        {...listeners}
        {...attributes}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span
            className="agenda-event-marker"
            style={{ width: 8, height: 8, background: eventColor, borderRadius: 999 }}
          />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1, minWidth: 0 }}>
            <Typography.Text style={{ fontWeight: 600 }} ellipsis>
              {evt.isOverlapping
                ? `${evt.title} +${evt.overlappingEvents?.length} outros`
                : evt.title}
              {evt.isMultiDay && (
                <span
                  style={{
                    fontSize: '10px',
                    color: '#005462',
                    marginLeft: '4px',
                    fontWeight: 'bold',
                  }}
                >
                  &nbsp;
                </span>
              )}
            </Typography.Text>
            {evt.isGrouped && evt.groupedEvents && evt.groupedEvents.length > 1 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                {evt.groupedEvents.slice(0, 3).map((groupedEvent, index) => {
                  if (groupedEvent.company_id === '48c8b458-f044-4796-b88f-0ddb23d087a0') {
                    return (
                      <div
                        key={groupedEvent.id}
                        style={{
                          width: '12px',
                          height: '12px',
                          borderRadius: '50%',
                          backgroundColor: '#005462',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          border: '1px solid #fff',
                          zIndex: 3 - index,
                          marginLeft: index > 0 ? '-4px' : '0',
                        }}
                      >
                        <span
                          style={{
                            fontSize: '6px',
                            color: '#fff',
                            fontWeight: 'bold',
                          }}
                        >
                          T
                        </span>
                      </div>
                    );
                  }

                  return (
                    <div
                      key={groupedEvent.id}
                      style={{
                        width: '12px',
                        height: '12px',
                        borderRadius: '50%',
                        backgroundColor: '#666',
                        border: '1px solid #fff',
                        zIndex: 3 - index,
                        marginLeft: index > 0 ? '-4px' : '0',
                      }}
                    />
                  );
                })}
                {evt.groupedEvents.length > 3 && (
                  <div
                    style={{
                      width: '12px',
                      height: '12px',
                      borderRadius: '50%',
                      backgroundColor: '#999',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '1px solid #fff',
                      marginLeft: '-4px',
                    }}
                  >
                    <span
                      style={{
                        fontSize: '6px',
                        color: '#fff',
                        fontWeight: 'bold',
                      }}
                    >
                      +{evt.groupedEvents.length - 3}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        {!evt.allDay && showTime && (
          <div style={{ fontSize: 11, opacity: 0.9 }}>
            {String(evt.startHour).padStart(2, '0')}:{String(evt.startMinute).padStart(2, '0')} -{' '}
            {String(endHour).padStart(2, '0')}:{String(endMinute).padStart(2, '0')}
          </div>
        )}
      </button>
    </Tooltip>
  );
};
